import type { ChartData } from 'chart.js'
import { DrillDimension, IChatManagementDrilldown, IDrillSelection, IOutcomeRow } from './models'
import {
  formatCount,
  formatHour,
  formatLocalDate,
  formatPercent,
  isBusinessHour,
  NO_RATE_TEXT,
  PALETTE,
  treatmentColor,
} from './viewModel'

/** Detay yığınının en çok derinliği (spec §8.6). */
export const MAX_DRILL_DEPTH = 6

/** Sunucunun beklediği yol biçimi: "outcome:error,intent:searchUrun" (ADR-022 §1). */
export const toDrillPath = (path: Pick<IDrillSelection, 'dimension' | 'key'>[]): string =>
  path.map(step => `${step.dimension}:${step.key}`).join(',')

/** Her adım bir öncekinin içinde daraltır; aynı boyut yolda iki kez yer alamaz (kesişim boş kalırdı). */
export const pushSelection = (stack: IDrillSelection[], next: IDrillSelection): IDrillSelection[] => {
  if (stack.some(step => step.dimension === next.dimension)) return stack
  const grown = [...stack, next]
  return grown.length > MAX_DRILL_DEPTH ? grown.slice(grown.length - MAX_DRILL_DEPTH) : grown
}

export const popSelection = (stack: IDrillSelection[]): IDrillSelection[] => stack.slice(0, -1)

export type DrillSection = 'outcomes' | 'intents' | 'daily' | 'hourly' | 'channels'

const OWN_SECTION: Partial<Record<DrillDimension, DrillSection>> = {
  outcome: 'outcomes',
  intent: 'intents',
  date: 'daily',
  hour: 'hourly',
  channel: 'channels',
}

/** Yolda zaten seçili boyutların bölümü tek satır olacağı (ve yeniden seçilemeyeceği) için gösterilmez. */
export const showsSection = (dimensions: DrillDimension[], section: DrillSection): boolean =>
  dimensions.every(dimension => OWN_SECTION[dimension] !== section)

export interface DrillTile {
  label: string
  value: string
}

/** Oran kutucukları değerlendirilen tur (iptal hariç) üzerindendir; hiç yoksa sunucunun 0'ı yerine "—" (spec §4.3). */
export const buildDrillTiles = (detail: IChatManagementDrilldown): DrillTile[] => {
  const { Summary } = detail
  const rate = (value: number): string => (Summary.EvaluatedTurns === 0 ? NO_RATE_TEXT : formatPercent(value))
  return [
    { label: 'Soru', value: formatCount(Summary.TotalTurns) },
    { label: 'Tüm soruların payı', value: formatPercent(Summary.ShareOfAll) },
    { label: 'Başarı oranı', value: rate(Summary.SuccessRate) },
    { label: 'Veri bulunamadı', value: rate(Summary.NoResultRate) },
    { label: 'Anlamadı (fallback)', value: rate(Summary.FallbackRate) },
  ]
}

const barStyle = { borderRadius: 4, borderSkipped: 'start' as const, maxBarThickness: 24 }

export const buildDrillDailyChart = (detail: IChatManagementDrilldown): ChartData<'bar', number[], string> => ({
  labels: detail.Daily.map(day => formatLocalDate(day.Date)),
  datasets: [
    { label: 'Soru', data: detail.Daily.map(day => day.Total), backgroundColor: PALETTE.success, ...barStyle },
  ],
})

export const buildDrillHourlyChart = (detail: IChatManagementDrilldown): ChartData<'bar', number[], string> => ({
  labels: detail.Hourly.map(row => formatHour(row.Hour)),
  datasets: [
    {
      label: 'Soru',
      data: detail.Hourly.map(row => row.Total),
      backgroundColor: detail.Hourly.map(row => (isBusinessHour(row.Hour) ? PALETTE.success : PALETTE.offHours)),
      ...barStyle,
    },
  ],
})

export const visibleOutcomes = (detail: IChatManagementDrilldown): IOutcomeRow[] =>
  detail.Outcomes.filter(row => row.Count > 0)

export const buildDrillOutcomeChart = (detail: IChatManagementDrilldown): ChartData<'bar', number[], string> => {
  const rows = visibleOutcomes(detail)
  return {
    labels: rows.map(row => row.Code),
    datasets: [
      {
        label: 'Adet',
        data: rows.map(row => row.Count),
        backgroundColor: rows.map(row => treatmentColor(row.SuccessTreatment)),
        ...barStyle,
      },
    ],
  }
}
